/**
 * Shared group color palette resolution for renderer widgets.
 *
 * Widgets that expose several visual groups can use this without coupling
 * themselves to each other. User-provided colors stay first; generated colors
 * only fill gaps.
 */

/**
 * Determine whether a hex color is "light" using relative luminance.
 * Supports 3-char (#rgb) and 6-char (#rrggbb) hex colors.
 */
export function isLightColor(hex: string): boolean {
  const clean = hex.replace('#', '');
  const full = clean.length === 3
    ? clean.split('').map(c => c + c).join('')
    : clean;

  if (!/^[0-9a-fA-F]{6}$/.test(full)) {
    return false;
  }

  const r = parseInt(full.slice(0, 2), 16) / 255;
  const g = parseInt(full.slice(2, 4), 16) / 255;
  const b = parseInt(full.slice(4, 6), 16) / 255;
  const lin = (c: number) => (c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4);
  const L = 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);

  return (1.05 / (L + 0.05)) < 4.5;
}

/** Text color to use on a given background for WCAG AA compliance. */
export function getTextColorForBackground(color: string): string {
  const hslLightness = parseHslLightness(color);
  if (hslLightness !== null) return hslLightness >= 60 ? '#1a1a1a' : '#ffffff';
  return isLightColor(color) ? '#1a1a1a' : '#ffffff';
}

function generateHslPalette(n: number): string[] {
  return Array.from({ length: n }, (_, i) => {
    const hue = Math.round(((i * 360) / n + 200) % 360);
    return `hsl(${hue}, 60%, 72%)`;
  });
}

/** Resolve the final palette of exactly `groupCount` colors. */
export function resolveGroupPalette(
  userColors: readonly string[] | undefined,
  groupCount: number,
): string[] {
  if (groupCount <= 0) return [];

  if (!userColors || userColors.length === 0) {
    return generateHslPalette(groupCount);
  }

  if (userColors.length >= groupCount) {
    return userColors.slice(0, groupCount) as string[];
  }

  const needed = groupCount - userColors.length;
  const extras = generateHslPalette(needed);
  return [...userColors, ...extras];
}

export interface DistinctGroupPaletteOptions {
  seedColors?: readonly string[];
  excludeColors?: readonly string[];
}

export function resolveDistinctGroupPalette(
  groupCount: number,
  options: DistinctGroupPaletteOptions = {},
): string[] {
  if (groupCount <= 0) return [];

  const used = new Set((options.excludeColors ?? []).map(normalizeColorToken));
  const palette: string[] = [];

  for (const color of options.seedColors ?? []) {
    const token = normalizeColorToken(color);
    if (used.has(token)) continue;
    palette.push(color);
    used.add(token);
    if (palette.length === groupCount) return palette;
  }

  const addCandidates = (candidates: readonly string[]) => {
    for (const color of candidates) {
      const token = normalizeColorToken(color);
      if (used.has(token)) continue;
      palette.push(color);
      used.add(token);
      if (palette.length === groupCount) return;
    }
  };

  addCandidates(generateHslPalette(groupCount));

  let candidateCount = groupCount + 1;
  while (palette.length < groupCount) {
    addCandidates(generateHslPalette(candidateCount++));
  }

  return palette;
}

export function getGroupClassName(groupIndex: number): string {
  return `widget-group-${groupIndex + 1}`;
}

function normalizeColorToken(color: string): string {
  return color.replace(/\s+/g, '').toLowerCase();
}

function parseHslLightness(color: string): number | null {
  const match = color.match(/^hsl\(\s*[\d.]+\s*,\s*[\d.]+%\s*,\s*([\d.]+)%\s*\)$/i);
  return match ? Number(match[1]) : null;
}
